import React, { useEffect, useMemo, useState } from 'react'
import { useQueryVehiclesList } from '@/lib/api/company/vehicle-list'
import { Vehicle } from '@/lib/api/company/vehicle-list/types'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type VehiclePickerProps = {
  companyId: string

  /** Initial selection */
  initial?: Vehicle[]

  /** When false -> radio (single). When true -> checkboxes (multiple). */
  multiple?: boolean

  /** Called with the new selection list */
  onSubmit: (selected: Vehicle[]) => void
  onCancel?: () => void

  /** Header texts */
  title?: string
  subtitleLeft?: string
  subtitleEntity?: string
  subtitleRight?: string

  /** Buttons */
  submitLabel?: string
  cancelLabel?: string

  disabled?: boolean
  className?: string
}

const EMPTY_VEHICLES: Vehicle[] = []

function normalize(str: string) {
  return str.toLowerCase().trim()
}

function getVehicleDisplayName(v: Vehicle) {
  // You can tune this format to your UI needs
  const reg = v.registrationNumber?.trim() ?? ''
  const make = v.make ?? ''
  const model = v.model ?? ''
  const mm = `${make} ${model}`.trim()

  if (mm && reg) return `${mm} • ${reg}`
  if (reg) return reg
  if (mm) return mm
  return `#${v.id}`
}

function getVehicleSecondary(v: Vehicle) {
  // secondary line / searchable fields
  const vin = v.vinNumber ?? ''
  const registry = v.registryNumber ?? ''
  const tachReg = v.registrationNumberFromTachograph ?? ''
  return [vin, registry, tachReg].filter(Boolean).join(' ')
}

export function VehiclePicker({
  companyId,
  initial,
  multiple = false,
  onSubmit,
  onCancel,
  title: titleProp,
  subtitleLeft = '',
  subtitleEntity = '',
  subtitleRight = '',
  submitLabel: submitLabelProp,
  cancelLabel: cancelLabelProp,
  disabled = false,
  className,
}: VehiclePickerProps) {
  const { t } = useLocalize()
  const title = titleProp ?? t('dashboard.vehicles')
  const submitLabel = submitLabelProp ?? t('common.save')
  const cancelLabel = cancelLabelProp ?? t('common.cancel')

  const {
    data: vehicles,
    isLoading,
    isError,
    // Import wiąże dane tylko z aktywnym pojazdem (jak w WPF).
  } = useQueryVehiclesList({ companyId, isActive: true })

  // Avoid "Maximum update depth exceeded" when initial is not provided
  const initialVehicles = initial ?? EMPTY_VEHICLES

  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(initialVehicles.map((v) => v.id)),
  )
  const [query, setQuery] = useState('')

  // Stable signature for initial selection (order-independent)
  const initialIdsKey = useMemo(() => {
    const ids = initialVehicles
      .map((v) => v.id)
      .slice()
      .sort((a, b) => a - b)
    return ids.join(',')
  }, [initialVehicles])

  // Sync selection only when initial IDs truly changed
  useEffect(() => {
    const next = new Set(initialVehicles.map((v) => v.id))

    setSelectedIds((prev) => {
      if (prev.size !== next.size) return next
      for (const id of prev) if (!next.has(id)) return next
      return prev
    })
  }, [initialIdsKey, initialVehicles])

  const allVehicles = useMemo(() => {
    if (Array.isArray(vehicles)) {
      return vehicles
    }

    return []
  }, [vehicles])

  const filteredVehicles = useMemo(() => {
    const q = normalize(query)
    if (!q) return allVehicles

    return allVehicles.filter((v) => {
      const name = normalize(getVehicleDisplayName(v))
      const reg = normalize(v.registrationNumber ?? '')
      const secondary = normalize(getVehicleSecondary(v))
      return name.includes(q) || reg.includes(q) || secondary.includes(q)
    })
  }, [allVehicles, query])

  const selectedVehicles = useMemo(
    () => allVehicles.filter((v) => selectedIds.has(v.id)),
    [allVehicles, selectedIds],
  )

  const selectedLabel = useMemo(() => {
    if (selectedVehicles.length === 0) return '—'
    if (selectedVehicles.length === 1)
      return getVehicleDisplayName(selectedVehicles[0])
    return selectedVehicles.map(getVehicleDisplayName).join(', ')
  }, [selectedVehicles])

  const toggle = (vehicleId: number) => {
    if (disabled) return

    setSelectedIds((prev) => {
      const next = new Set(prev)

      if (multiple) {
        if (next.has(vehicleId)) next.delete(vehicleId)
        else next.add(vehicleId)
        return next
      }

      // single selection: radio behavior
      return new Set<number>([vehicleId])
    })
  }

  const handleSubmit = () => {
    if (disabled) return
    onSubmit(selectedVehicles)
  }

  const handleCancel = () => {
    onCancel?.()
  }

  return (
    <div className={['tw-w-full', className].filter(Boolean).join(' ')}>
      {/* Header */}
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
        <div className="tw-min-w-0">
          <div className="tw-text-base tw-font-semibold tw-text-zinc-900">
            {title}
          </div>

          <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-xs tw-text-zinc-500">
            <span>{subtitleLeft}</span>
            {subtitleEntity ? (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-zinc-700">
                <span className="tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded tw-bg-zinc-100 tw-text-[10px]">
                  ✓
                </span>
                <span className="tw-font-medium">{subtitleEntity}</span>
              </span>
            ) : null}
            <span>{subtitleRight}</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-border-zinc-200 tw-bg-white tw-px-2 tw-py-1.5">
        {/* Search icon */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M7.25 12.5a5.25 5.25 0 1 1 0-10.5 5.25 5.25 0 0 1 0 10.5Z"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M11.2 11.2 14 14"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </svg>

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('shared.vehicle-picker.search-placeholder')}
          className="tw-w-full tw-border-0 tw-bg-transparent tw-text-sm tw-text-zinc-900 tw-outline-none placeholder:tw-text-zinc-400"
          disabled={disabled}
        />
      </div>

      {/* Table */}
      <div className="tw-mt-3 tw-overflow-hidden tw-rounded tw-border tw-border-zinc-200">
        <div className="tw-max-h-[340px] tw-overflow-auto">
          <table className="tw-w-full tw-border-separate tw-border-spacing-0">
            <thead className="tw-sticky tw-top-0 tw-bg-zinc-50">
              <tr className="tw-text-left tw-text-[11px] tw-font-medium tw-text-zinc-500">
                <th className="tw-w-[44px] tw-border-b tw-border-zinc-200 tw-px-3 tw-py-2" />
                <th className="tw-border-b tw-border-zinc-200 tw-px-3 tw-py-2">
                  <div className="tw-flex tw-items-center tw-gap-1">
                    <span>{t('common.vehicle').toUpperCase()}</span>
                    <span className="tw-inline-flex tw-flex-col tw-leading-none tw-text-zinc-400">
                      <span>▲</span>
                      <span>▼</span>
                    </span>
                  </div>
                </th>
                <th className="tw-border-b tw-border-zinc-200 tw-px-3 tw-py-2">
                  {t('shared.picker.db-status')}
                </th>
                <th className="tw-border-b tw-border-zinc-200 tw-px-3 tw-py-2">
                  {t('shared.vehicle-picker.reg-number')}
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={4}
                    className="tw-px-3 tw-py-6 tw-text-sm tw-text-zinc-500"
                  >
                    {t('shared.vehicle-picker.loading')}
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td
                    colSpan={4}
                    className="tw-px-3 tw-py-6 tw-text-sm tw-text-red-600"
                  >
                    {t('shared.vehicle-picker.error')}
                  </td>
                </tr>
              ) : filteredVehicles.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="tw-px-3 tw-py-6 tw-text-sm tw-text-zinc-500"
                  >
                    {t('shared.picker.no-results')}
                    {query ? (
                      <>
                        {' '}
                        {t('shared.picker.no-results-for')}:{' '}
                        <span className="tw-font-medium tw-text-zinc-800">
                          {query}
                        </span>
                      </>
                    ) : null}
                  </td>
                </tr>
              ) : (
                filteredVehicles.map((v) => {
                  const checked = selectedIds.has(v.id)
                  const isActive = v.isActive

                  return (
                    <tr
                      key={v.id}
                      className={[
                        'tw-cursor-pointer',
                        'hover:tw-bg-zinc-50',
                        checked ? 'tw-bg-zinc-50' : '',
                      ].join(' ')}
                      onClick={() => toggle(v.id)}
                      role="button"
                      aria-pressed={checked}
                    >
                      <td className="tw-border-b tw-border-zinc-200 tw-px-3 tw-py-2 tw-align-middle">
                        {multiple ? (
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(v.id)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={disabled}
                            className="tw-h-4 tw-w-4"
                          />
                        ) : (
                          <input
                            type="radio"
                            name="vehicle-picker"
                            checked={checked}
                            onChange={() => toggle(v.id)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={disabled}
                            className="tw-h-4 tw-w-4"
                          />
                        )}
                      </td>

                      <td className="tw-border-b tw-border-zinc-200 tw-px-3 tw-py-2 tw-align-middle">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          {/* simple "car" icon */}
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                            aria-hidden="true"
                            className="tw-text-zinc-400"
                          >
                            <path
                              d="M3.2 10.5h9.6M4 10.5l.7-2.1c.2-.7.8-1.1 1.5-1.1h3.6c.7 0 1.3.4 1.5 1.1l.7 2.1"
                              stroke="currentColor"
                              strokeWidth="1.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M3 10.5v2c0 .6.4 1 1 1h.5M13 10.5v2c0 .6-.4 1-1 1h-.5"
                              stroke="currentColor"
                              strokeWidth="1.2"
                              strokeLinecap="round"
                            />
                            <path
                              d="M5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM11 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                              stroke="currentColor"
                              strokeWidth="1.2"
                            />
                          </svg>

                          <span className="tw-text-sm tw-font-medium tw-text-zinc-900">
                            {getVehicleDisplayName(v)}
                          </span>
                        </div>

                        {getVehicleSecondary(v) ? (
                          <div className="tw-mt-0.5 tw-text-xs tw-text-zinc-500">
                            {getVehicleSecondary(v)}
                          </div>
                        ) : null}
                      </td>

                      <td className="tw-border-b tw-border-zinc-200 tw-px-3 tw-py-2 tw-align-middle">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                          {isActive ? (
                            <>
                              <span className="tw-text-emerald-600">✓</span>
                              <span className="tw-text-emerald-700">
                                {t('common.active')}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="tw-text-zinc-400">✕</span>
                              <span className="tw-text-zinc-600">
                                {t('common.inactive')}
                              </span>
                            </>
                          )}
                        </div>
                      </td>

                      <td className="tw-border-b tw-border-zinc-200 tw-px-3 tw-py-2 tw-align-middle tw-text-sm tw-text-zinc-700">
                        {v.registrationNumber?.trim() || '—'}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-zinc-200 tw-bg-white tw-px-3 tw-py-3">
          <div className="tw-min-w-0 tw-text-xs tw-text-zinc-600">
            {t('shared.vehicle-picker.selected')}:{' '}
            <span className="tw-font-medium tw-text-zinc-900">
              {selectedLabel}
            </span>
          </div>

          <div className="tw-flex tw-items-center tw-gap-2">
            {onCancel ? (
              <button
                type="button"
                className="tw-rounded tw-border tw-border-zinc-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-zinc-800 hover:tw-bg-zinc-50 disabled:tw-opacity-50"
                onClick={handleCancel}
                disabled={disabled}
              >
                {cancelLabel}
              </button>
            ) : null}

            <button
              type="button"
              className="tw-rounded tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50"
              onClick={handleSubmit}
              disabled={disabled || selectedVehicles.length === 0}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
